import type { AchievementSummary, AdminUser, AgentRun, AgentRunSource, ContentPoint, DashboardMetric, DeviceSummary, LiveTrip, Role, RouteSummary, TraceEvent, TripTimeline, UserSummary } from '@/types'
import { preview } from '@preview-runtime'
import { sha256File } from './sha256'

const configuredApiBase = (import.meta.env.VITE_API_BASE ?? '/api/v2').replace(/\/$/, '')
if (import.meta.env.MODE !== 'development' && !/^\/(?!\/)/.test(configuredApiBase)) {
  throw new Error('integration / production 的 VITE_API_BASE 必须是同源绝对路径。')
}
const API_BASE = configuredApiBase
export const AUTH_EXPIRED_EVENT = 'localike:auth-expired'

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly requestId?: string,
    public readonly code?: string,
    public readonly details?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

export function formatApiError(error: unknown, fallback: string): string {
  if (!(error instanceof ApiError)) return fallback
  return `${error.message}${error.requestId ? `（请求编号：${error.requestId}）` : ''}`
}

export interface ApiFieldError {
  path: string
  message: string
}

/** 兼容 Pydantic loc/msg 与领域 field/path/message，只用于将真实服务端拒绝定位到表单。 */
export function apiFieldErrors(error: unknown): ApiFieldError[] {
  if (!(error instanceof ApiError) || error.details === null || error.details === undefined) return []
  const results: ApiFieldError[] = []
  const visit = (value: unknown): void => {
    if (Array.isArray(value)) {
      value.forEach(visit)
      return
    }
    if (!value || typeof value !== 'object') return
    const item = value as Record<string, unknown>
    const rawPath = item.loc ?? item.path ?? item.field
    const rawMessage = item.msg ?? item.message
    const path = Array.isArray(rawPath) ? rawPath.map(String).filter((part) => !['body', 'changes', 'data'].includes(part)).join('.') : typeof rawPath === 'string' ? rawPath : ''
    if (path && typeof rawMessage === 'string') results.push({ path, message: rawMessage })
    for (const [key, child] of Object.entries(item)) {
      if (!['loc', 'path', 'field', 'msg', 'message'].includes(key)) visit(child)
    }
  }
  visit(error.details)
  return results
}

function randomId(): string {
  return globalThis.crypto?.randomUUID?.() ?? `local-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

function cookieValue(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined
  const item = document.cookie.split('; ').find((value) => value.startsWith(`${name}=`))
  return item ? decodeURIComponent(item.slice(name.length + 1)) : undefined
}

type MockFactory<T> = () => T | Promise<T>

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown
  rawBody?: BodyInit
  idempotent?: boolean
  skipAuthRefresh?: boolean
  suppressAuthExpiredEvent?: boolean
  preserveEnvelope?: boolean
}

export interface CollectionQuery {
  page: number
  pageSize: number
  sort?: string
  filter?: string
}

export interface CollectionPage<T> {
  items: T[]
  total: number
  totalPages: number
  nextCursor: string | number | null
  hasNext: boolean
  page: number
  pageSize: number
}

export interface CursorCollectionQuery {
  cursor?: string
  limit: number
  sort?: string
  filter?: string
}

export interface CursorCollectionPage<T> {
  items: T[]
  count: number
  cursor: string
  nextCursor: string | number | null
  hasNext: boolean
}

export interface SummaryCollectionMeta {
  applied_limit: number
  has_more: boolean
}

export interface SummaryEnvelope<T, C extends Record<string, SummaryCollectionMeta>> {
  data: T
  meta: {
    scope: string
    window: string
    subject_id?: string
    collections: C
  }
}

interface CollectionEnvelope<T> {
  data: T[]
  request_id?: string
  meta?: {
    count?: number
    total?: number
    next_cursor?: string | number | null
    page?: number
    page_size?: number
    total_pages?: number
    has_next?: boolean
  } | null
}

export function collectionQueryString(query: CollectionQuery): string {
  const params = new URLSearchParams({
    page: String(Math.max(1, Math.trunc(query.page))),
    page_size: String(Math.max(1, Math.trunc(query.pageSize))),
  })
  if (query.sort) params.set('sort', query.sort)
  if (query.filter) params.set('filter', query.filter)
  return params.toString()
}

function isInteger(value: unknown, minimum: number): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= minimum
}

export function normalizeCollectionPage<T>(payload: CollectionEnvelope<T>, requested: CollectionQuery): CollectionPage<T> {
  const data = payload.data
  const meta = payload.meta
  if (
    !Array.isArray(data)
    ||
    !meta
    || !isInteger(meta.total, 0)
    || !isInteger(meta.page, 1)
    || !isInteger(meta.page_size, 1)
    || !isInteger(meta.total_pages, 0)
    || typeof meta.has_next !== 'boolean'
  ) {
    throw new ApiError(
      '分页列表缺少 page / page_size / total / total_pages / has_next 元数据，无法判断完整集合。',
      502,
      payload.request_id,
      'invalid_collection_contract',
    )
  }
  const expectedTotalPages = meta.total === 0 ? 0 : Math.ceil(meta.total / meta.page_size)
  const expectedHasNext = meta.page < expectedTotalPages
  if (meta.page !== requested.page || meta.page_size !== requested.pageSize) {
    throw new ApiError(
      '分页响应与请求的 page / page_size 不一致。',
      502,
      payload.request_id,
      'invalid_collection_contract',
    )
  }
  if (
    meta.total_pages !== expectedTotalPages
    || meta.has_next !== expectedHasNext
    || (meta.count !== undefined && meta.count !== data.length)
    || data.length > meta.page_size
    || (meta.has_next && data.length === 0)
  ) {
    throw new ApiError(
      '分页响应的数据窗口与元数据不一致。',
      502,
      payload.request_id,
      'invalid_collection_contract',
    )
  }
  return {
    items: data,
    total: meta.total,
    totalPages: meta.total_pages,
    nextCursor: meta?.next_cursor ?? null,
    hasNext: meta.has_next,
    page: meta.page,
    pageSize: meta.page_size,
  }
}

function mapCollectionPage<T, U>(page: CollectionPage<T>, mapper: (item: T, index: number) => U): CollectionPage<U> {
  return { ...page, items: page.items.map(mapper) }
}

function mapCursorPage<T, U>(page: CursorCollectionPage<T>, mapper: (item: T, index: number) => U): CursorCollectionPage<U> {
  return { ...page, items: page.items.map(mapper) }
}

export function cursorCollectionQueryString(query: CursorCollectionQuery): string {
  const params = new URLSearchParams({ limit: String(Math.max(1, Math.trunc(query.limit))) })
  if (query.cursor) params.set('after_cursor', query.cursor)
  if (query.sort) params.set('sort', query.sort)
  if (query.filter) params.set('filter', query.filter)
  return params.toString()
}

export function normalizeCursorCollectionPage<T>(payload: CollectionEnvelope<T>, requested: CursorCollectionQuery): CursorCollectionPage<T> {
  const data = payload.data
  const nextCursor = payload.meta?.next_cursor
  if (
    !Array.isArray(data)
    ||
    !payload.meta
    || typeof payload.meta.has_next !== 'boolean'
    || !('next_cursor' in payload.meta)
    || (payload.meta.count !== undefined && payload.meta.count !== data.length)
    || data.length > Math.max(1, Math.trunc(requested.limit))
    || (payload.meta.has_next && (data.length === 0 || nextCursor === null || nextCursor === undefined || nextCursor === '' || String(nextCursor) === requested.cursor))
  ) {
    throw new ApiError('游标列表缺少 has_next / next_cursor 元数据，无法判断是否已读完。', 502, payload.request_id, 'invalid_collection_contract')
  }
  return {
    items: data,
    count: data.length,
    cursor: requested.cursor ?? '',
    nextCursor: nextCursor ?? null,
    hasNext: payload.meta.has_next,
  }
}

interface ErrorPayload {
  error?: {
    code?: string
    message?: string
    request_id?: string
    details?: unknown
  }
}

let refreshPromise: Promise<boolean> | null = null

async function refreshAdminSession(): Promise<boolean> {
  if (refreshPromise) return refreshPromise
  refreshPromise = (async () => {
    const headers = new Headers({
      Accept: 'application/json',
      'X-Request-ID': randomId(),
      'Idempotency-Key': randomId(),
    })
    const csrf = cookieValue('localike_csrf')
    if (csrf) headers.set('X-CSRF-Token', csrf)
    try {
      const response = await fetch(`${API_BASE}/auth/admin/refresh`, {
        method: 'POST',
        credentials: 'include',
        cache: 'no-store',
        headers,
      })
      if (!response.ok) return false
      if (response.status === 204) return true
      const payload = await response.json().catch(() => null)
      return Boolean(payload && typeof payload === 'object' && !Array.isArray(payload) && 'data' in payload)
    } catch {
      return false
    } finally {
      refreshPromise = null
    }
  })()
  return refreshPromise
}

async function request<T>(path: string, options: RequestOptions = {}, mock?: MockFactory<T>): Promise<T> {
  const method = (options.method ?? 'GET').toUpperCase()
  const requestId = randomId()
  const headers = new Headers(options.headers)
  headers.set('Accept', 'application/json')
  headers.set('X-Request-ID', requestId)
  if (options.body !== undefined) headers.set('Content-Type', 'application/json')
  if (options.idempotent ?? method !== 'GET') headers.set('Idempotency-Key', randomId())

  const csrf = cookieValue('localike_csrf')
  if (csrf) headers.set('X-CSRF-Token', csrf)

  // preview.enabled 只会在 development 构建中为真；integration/production 的别名指向 strict provider。
  if (preview.enabled && mock) {
    await new Promise((resolve) => window.setTimeout(resolve, 120))
    return mock()
  }

  const { body, rawBody, idempotent: _idempotent, skipAuthRefresh, suppressAuthExpiredEvent, preserveEnvelope, ...fetchOptions } = options
  const perform = () => {
    // refresh 会旋转 CSRF cookie，因此每次实际发送前都要重新读取，
    // 不能让 401 后的原请求携带已失效的旧 token。
    const attemptHeaders = new Headers(headers)
    const latestCsrf = cookieValue('localike_csrf')
    if (latestCsrf) attemptHeaders.set('X-CSRF-Token', latestCsrf)
    else attemptHeaders.delete('X-CSRF-Token')
    return fetch(`${API_BASE}${path}`, {
      ...fetchOptions,
      method,
      credentials: 'include',
      cache: 'no-store',
      headers: attemptHeaders,
      body: rawBody ?? (body === undefined ? undefined : JSON.stringify(body)),
    })
  }

  try {
    let response = await perform()
    if (response.status === 401 && !skipAuthRefresh && !path.startsWith('/auth/admin/')) {
      if (await refreshAdminSession()) response = await perform()
    }

    if (response.status === 401 && !skipAuthRefresh && !suppressAuthExpiredEvent && !path.startsWith('/auth/admin/')) {
      window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT))
    }

    const responseRequestId = response.headers.get('x-request-id') ?? requestId
    if (!response.ok) {
      const payload = await response.json().catch(() => null) as ErrorPayload | null
      if (!payload?.error || typeof payload.error.code !== 'string' || typeof payload.error.message !== 'string' || typeof payload.error.request_id !== 'string') {
        throw new ApiError('服务端错误响应不符合 Localike 2.0 envelope 契约。', response.status, responseRequestId, 'invalid_error_contract')
      }
      throw new ApiError(
        payload.error.message,
        response.status,
        payload.error.request_id,
        payload.error.code,
        payload.error.details,
      )
    }

    if (response.status === 204) return undefined as T
    const payload = await response.json().catch(() => null)
    if (!payload || typeof payload !== 'object' || Array.isArray(payload) || !('data' in payload)) {
      throw new ApiError('服务端成功响应不符合 Localike 2.0 envelope 契约。', 502, responseRequestId, 'invalid_response_contract')
    }
    if (preserveEnvelope) return payload as T
    return (payload as { data: T }).data
  } catch (error) {
    if (error instanceof ApiError) throw error
    throw new ApiError('无法连接 Localike API，请检查网络或服务健康状态后重试。', 0, requestId, 'network_error')
  }
}

export async function requestCollection<T>(path: string, query: CollectionQuery, mock?: MockFactory<T[]>): Promise<CollectionPage<T>> {
  if (preview.enabled && mock) {
    const items = await mock()
    const start = (query.page - 1) * query.pageSize
    const totalPages = Math.ceil(items.length / query.pageSize)
    return {
      items: items.slice(start, start + query.pageSize),
      total: items.length,
      totalPages,
      nextCursor: null,
      hasNext: query.page < totalPages,
      page: query.page,
      pageSize: query.pageSize,
    }
  }
  const separator = path.includes('?') ? '&' : '?'
  const payload = await request<CollectionEnvelope<T>>(`${path}${separator}${collectionQueryString(query)}`, { preserveEnvelope: true })
  return normalizeCollectionPage(payload, query)
}

export async function requestCursorCollection<T>(
  path: string,
  query: CursorCollectionQuery,
  mock?: MockFactory<T[]>,
  cursorParameter = 'after_cursor',
): Promise<CursorCollectionPage<T>> {
  if (preview.enabled && mock) {
    const items = await mock()
    const offset = Number.parseInt(query.cursor ?? '0', 10)
    const start = Number.isFinite(offset) && offset >= 0 ? offset : 0
    const window = items.slice(start, start + query.limit)
    const next = start + window.length
    return { items: window, count: window.length, cursor: query.cursor ?? '', nextCursor: next < items.length ? String(next) : null, hasNext: next < items.length }
  }
  const params = new URLSearchParams({ limit: String(Math.max(1, Math.trunc(query.limit))) })
  if (query.cursor) params.set(cursorParameter, query.cursor)
  if (query.sort) params.set('sort', query.sort)
  if (query.filter) params.set('filter', query.filter)
  const separator = path.includes('?') ? '&' : '?'
  const payload = await request<CollectionEnvelope<T>>(`${path}${separator}${params}`, { preserveEnvelope: true })
  return normalizeCursorCollectionPage(payload, query)
}

async function requestSummary<T, C extends Record<string, SummaryCollectionMeta>>(
  path: string,
  expectedCollections: readonly (keyof C & string)[],
  mock?: MockFactory<T>,
): Promise<SummaryEnvelope<T, C>> {
  if (preview.enabled && mock) {
    const data = await mock()
    const collections = Object.fromEntries(expectedCollections.map((key) => {
      const value = (data as Record<string, unknown>)[key]
      return [key, { applied_limit: Array.isArray(value) ? Math.max(1, value.length) : 1, has_more: false }]
    })) as C
    return { data, meta: { scope: 'development_preview', window: 'all_time', collections } }
  }
  const payload = await request<SummaryEnvelope<T, C>>(path, { preserveEnvelope: true })
  if (
    !payload.data
    || typeof payload.data !== 'object'
    || Array.isArray(payload.data)
    || !payload.meta
    || !payload.meta.collections
    || typeof payload.meta.scope !== 'string'
    || typeof payload.meta.window !== 'string'
  ) {
    throw new ApiError('汇总响应缺少 scope / window / collections 元数据。', 502, undefined, 'invalid_summary_contract')
  }
  for (const key of expectedCollections) {
    const meta = payload.meta.collections[key]
    const rows = (payload.data as Record<string, unknown>)[key]
    if (
      !meta
      || !isInteger(meta.applied_limit, 1)
      || typeof meta.has_more !== 'boolean'
      || !Array.isArray(rows)
      || rows.length > meta.applied_limit
    ) {
      throw new ApiError(`汇总集合 ${key} 缺少 applied_limit / has_more 元数据。`, 502, undefined, 'invalid_summary_contract')
    }
  }
  return payload
}

interface RawDashboard {
  routes: number
  pois: number
  active_trips: number
  devices: number
  online_devices: number
  agent_runs: number
  pending_outbox: number
}

export interface RawRoute {
  id: string
  name: string
  city: string
  revision: number
  waypoint_ids: string[]
  hidden_waypoint_ids: string[]
  sound_effect_ids: string[]
  status: RouteSummary['status']
  updated_at: string
}

export interface RawPoi {
  id: string
  name: string
  city?: string
  description: string
  latitude: number
  longitude: number
  revision: number
  status: 'draft' | 'published' | 'archived'
  tags?: string[]
  metadata?: Record<string, unknown>
  updated_at?: string
}

export interface RawRhythmPoint {
  id: string
  name: string
  route_id: string
  poi_id: string | null
  sequence: number
  dwell_seconds: number
  instruction: string
  status: 'draft' | 'published' | 'archived'
  revision: number
  metadata?: Record<string, unknown>
  updated_at: string
}

export interface RawMusicPoint {
  id: string
  name: string
  route_id: string
  poi_id: string | null
  asset_id: string
  sequence: number
  offset_seconds: number
  duration_seconds: number | null
  status: 'draft' | 'published' | 'archived'
  revision: number
  metadata?: Record<string, unknown>
  updated_at: string
}

export interface RawKnowledge {
  id: string
  title: string
  body: string
  tags: string[]
  poi_ids: string[]
  route_ids: string[]
  status: 'draft' | 'published' | 'archived'
  revision: number
  metadata?: Record<string, unknown>
  updated_at: string
}

export interface ContentValidation {
  valid: boolean
  errors: Array<{ code: string; message: string; details?: unknown }>
  warnings: Array<{ code: string; message: string; details?: unknown }>
  closure: Record<string, unknown>
  content_hash: string
  revision: number
}

export interface ContentRevision {
  id: string
  content_id: string
  kind: string
  revision: number
  content_hash: string
  closure: Record<string, unknown>
  created_at: string
}

export interface ContentDiff {
  from_revision: number
  to_revision: number
  from_content_hash: string
  to_content_hash: string
  changed: boolean
  changes: Array<{ path: string; before: unknown; after: unknown }>
}

export interface AgentToolContract {
  name: string
  description: string
  input_schema: Record<string, unknown>
  mutating: boolean
  permission: string
  timeout_ms: number
  unknown_outcome_policy: 'safe_retry' | 'receipt_lookup_required'
  evaluation_policy: 'allowed_with_simulated_facts' | 'blocked'
}

export interface PromptEvaluationTarget {
  schema_version: 1
  prompt_id: string
  prompt_revision: number
  prompt_content_hash: string
  tool_policy_hash: string
  tool_allowlist: string[]
}

export interface PromptEvaluationEvidence {
  run_id: string
  run_status: 'queued' | 'running' | 'completed' | 'failed' | 'cancelled' | 'timed_out' | 'unknown_outcome'
  purpose: string
  expected_target: PromptEvaluationTarget
  recorded_target: PromptEvaluationTarget | null
  evaluation_context_present: boolean
  terminal_event: string | null
  terminal_cursor: number
  event_count: number
  tool_call_count: number
  failure_event_count: number
  failure_counts: Record<string, number>
  passed: boolean
  reasons: string[]
}

function isPromptEvaluationTarget(value: unknown): value is PromptEvaluationTarget {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false
  const target = value as Record<string, unknown>
  return target.schema_version === 1
    && typeof target.prompt_id === 'string'
    && target.prompt_id.length > 0
    && isInteger(target.prompt_revision, 1)
    && typeof target.prompt_content_hash === 'string'
    && /^[a-f0-9]{64}$/.test(target.prompt_content_hash)
    && typeof target.tool_policy_hash === 'string'
    && /^[a-f0-9]{64}$/.test(target.tool_policy_hash)
    && Array.isArray(target.tool_allowlist)
    && target.tool_allowlist.every((item) => typeof item === 'string')
}

export function normalizePromptEvaluationEvidence(value: unknown): PromptEvaluationEvidence {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new ApiError('Prompt 评测证据响应不是对象。', 502, undefined, 'invalid_prompt_evaluation_contract')
  }
  const evidence = value as Record<string, unknown>
  const statuses = new Set(['queued', 'running', 'completed', 'failed', 'cancelled', 'timed_out', 'unknown_outcome'])
  const failureCounts = evidence.failure_counts
  const reasons = evidence.reasons
  const recordedTarget = evidence.recorded_target
  const countsValid = failureCounts !== null
    && typeof failureCounts === 'object'
    && !Array.isArray(failureCounts)
    && Object.values(failureCounts).every((count) => isInteger(count, 0))
  const failureSum = countsValid
    ? Object.values(failureCounts as Record<string, number>).reduce((sum, count) => sum + count, 0)
    : -1
  if (
    typeof evidence.run_id !== 'string'
    || evidence.run_id.length === 0
    || typeof evidence.run_status !== 'string'
    || !statuses.has(evidence.run_status)
    || typeof evidence.purpose !== 'string'
    || !isPromptEvaluationTarget(evidence.expected_target)
    || (recordedTarget !== null && !isPromptEvaluationTarget(recordedTarget))
    || typeof evidence.evaluation_context_present !== 'boolean'
    || (evidence.terminal_event !== null && typeof evidence.terminal_event !== 'string')
    || !isInteger(evidence.terminal_cursor, 0)
    || !isInteger(evidence.event_count, 0)
    || !isInteger(evidence.tool_call_count, 0)
    || !isInteger(evidence.failure_event_count, 0)
    || !countsValid
    || failureSum !== evidence.failure_event_count
    || typeof evidence.passed !== 'boolean'
    || !Array.isArray(reasons)
    || !reasons.every((reason) => typeof reason === 'string')
    || evidence.passed !== (reasons.length === 0)
    || (evidence.passed && (
      evidence.run_status !== 'completed'
      || evidence.purpose !== 'evaluation'
      || evidence.evaluation_context_present !== true
      || evidence.terminal_event !== 'run.completed'
      || evidence.failure_event_count !== 0
      || JSON.stringify(recordedTarget) !== JSON.stringify(evidence.expected_target)
    ))
  ) {
    throw new ApiError('Prompt 评测证据缺少权威聚合字段或内部不一致。', 502, undefined, 'invalid_prompt_evaluation_contract')
  }
  return value as PromptEvaluationEvidence
}

export type ManagedAgentConfigKind = 'prompt_config' | 'knowledge_config' | 'memory_template' | 'trip_policy' | 'price_config' | 'asr_vocabulary'

export interface ManagedAgentConfig {
  id: string
  kind: ManagedAgentConfigKind
  external_key: string | null
  state: string
  revision: number
  created_at: string
  updated_at: string
  [key: string]: unknown
}

export type AsrVocabularySourceType = 'guide_name' | 'knowledge_base_name' | 'knowledge_entry_name' | 'poi_name' | 'poi_nearby_name' | 'route_name'

export type AsrVocabularyEntry =
  | { kind: 'custom'; term: string; weight: number; enabled: boolean }
  | {
      kind: 'source_ref'
      source_type: AsrVocabularySourceType
      source_id: string
      weight: number | null
      enabled: boolean
      resolved_term?: string | null
      source_revision_id?: string | null
      source_revision?: number | null
      source_content_hash?: string | null
    }

export interface AsrVocabularySourceConfig {
  source_type: AsrVocabularySourceType
  default_weight: number
  enabled: boolean
}

export interface PublicAsrVocabulary extends ManagedAgentConfig {
  kind: 'asr_vocabulary'
  name: string
  provider_code: string
  locale: string
  priority: number
  entries: AsrVocabularyEntry[]
  source_configs: AsrVocabularySourceConfig[]
  status: 'draft' | 'published' | 'archived'
  enabled: boolean
  review_status: 'pending' | 'approved' | 'rejected'
  reviewed_by: string | null
  reviewed_at: string | null
  review_reason: string | null
}

export interface AsrVocabularySnapshot {
  vocabulary_id: string
  vocabulary_revision: number
  content_hash: string
  provider_code: string
  locale: string
  priority: number
  terms: Array<{
    term: string
    weight: number
    source_type: 'custom' | AsrVocabularySourceType
    source_id: string | null
    source_revision_id: string | null
    source_revision: number | null
    source_content_hash: string | null
  }>
}

export interface KnowledgeImportFieldError {
  field: string
  code: string
  value?: unknown
  message?: string
}

export interface KnowledgeImportRow {
  line: number
  status: 'valid' | 'rejected'
  errors: KnowledgeImportFieldError[]
  title?: string | null
  body_preview?: string
}

export interface KnowledgeImportPreview {
  import_id: string
  batch_id: string
  file_sha256: string
  rows_sha256: string
  total_rows: number
  valid_rows: number
  error_rows: number
  can_apply: boolean
  rows: KnowledgeImportRow[]
  replayed: boolean
  status?: 'previewed' | 'applied' | 'rejected'
}

export interface KnowledgeImportReceipt {
  id: string
  state: 'previewed' | 'applied' | 'rejected'
  revision: number
  batch_id: string
  file_sha256: string
  rows_sha256: string
  file_format: 'csv' | 'jsonl'
  status: 'previewed' | 'applied' | 'rejected'
  total_rows: number
  valid_rows: number
  error_rows: number
  errors: Array<{ line: number; errors: KnowledgeImportFieldError[] }>
  imported_ids: string[]
  created_at: string
  updated_at: string
}

export interface KnowledgeImportApplyResult {
  import_id: string
  status: 'applied'
  inserted: number
  unchanged: number
  imported_ids: string[]
  replayed: boolean
}

interface RawTrip {
  id: string
  user_id: string
  mode: LiveTrip['mode']
  status: 'active' | 'paused' | 'ended' | 'deleted'
  route_id: string | null
  device_id?: string | null
  runtime_generation?: number
  plans?: Array<{ revision: number }>
  metadata?: Record<string, unknown>
  created_at?: string
  updated_at: string
}

interface RawAgentRun {
  id: string
  trip_id: string | null
  purpose: string
  status: 'queued' | 'running' | 'completed' | 'failed' | 'cancelled' | 'timed_out' | 'unknown_outcome'
  provider: string
  model: string
  input?: Record<string, unknown>
  usage?: Record<string, unknown>
  created_at: string
  updated_at: string
}

interface RawAgentEvent {
  cursor: number
  kind: string
  data: Record<string, unknown>
  created_at: string
}

export interface AgentEventStreamHandlers {
  onEvent: (event: TraceEvent) => void
  onDone: (reason: 'done' | 'timeout' | 'closed', cursor: number) => void
  onError: (error: ApiError) => void
}

export interface RawGuide {
  id: string
  name: string
  persona: string
  voice: string | null
  status: string
  revision: number
}

export interface RawPrompt {
  id: string
  name: string
  purpose: string
  template: string
  tool_allowlist: string[]
  status: string
  revision: number
}

export interface RawTrigger {
  id: string
  name: string
  event_type: string
  radius_meters: number
  cooldown_seconds: number
  prompt_id: string | null
  prompt_config_id?: string | null
  poi_ids?: string[]
  knowledge_ids?: string[]
  dwell_seconds?: number
  daily_limit?: number
  max_accuracy_meters?: number
  modes?: string[]
  priority?: number
  enabled?: boolean
  auto_trigger_enabled?: boolean
  allow_out_of_order?: boolean
  vibration_enabled?: boolean
  vibration_pattern?: string | null
  per_poi_limit?: number | null
  per_trip_limit?: number | null
  approaching_radius_meters?: number | null
  conditions?: {
    time_windows?: unknown[]
    weather?: {
      conditions?: string[]
      min_temperature_c?: number | null
      max_temperature_c?: number | null
      min_precipitation_mm?: number | null
      max_wind_speed_mps?: number | null
      max_age_seconds?: number
    } | null
    motion?: {
      states?: string[]
      min_speed_mps?: number | null
      max_speed_mps?: number | null
    } | null
    min_trip_elapsed_seconds?: number
    min_idle_seconds?: number
    max_distance_to_poi_meters?: number | null
    required_prior_trigger_ids?: string[]
    min_prior_trigger_count?: number
    deviation_distance_threshold_meters?: number | null
    weather_temperature_delta_threshold_c?: number | null
    weather_temperature_delta_enabled?: boolean
    weather_condition_change_enabled?: boolean
    fatigue_first_trigger_minutes?: number | null
    fatigue_repeat_interval_minutes?: number | null
  }
  status: string
  revision: number
  metadata?: Record<string, unknown>
}

interface RawDevice {
  id: string
  serial_number?: string | null
  status: 'provisioned' | 'claimed' | 'online' | 'offline' | 'revoked'
  owner_user_id?: string | null
  capabilities?: Record<string, unknown>
  firmware_version?: string | null
  runtime_generation?: number
  connection_generation?: number
  config_revision?: number
  last_seen_at?: string | null
  updated_at?: string | null
}

export interface DeviceEventRecord {
  id: string
  device_id: string
  session_id: string
  client_event_id: string
  seq: number
  runtime_generation: number
  message_type: string
  recorded_at: string
  received_at: string
  payload_hash: string
  data: Record<string, unknown>
  ack_cursor: string
}

interface RawManagedUser {
  id: string
  name: string
  status: UserSummary['status']
  locale: string
  tags?: string[]
  revision: number
  updated_at: string
}

export interface ManagedCampaign {
  id: string
  title: string
  channel: string
  status: 'draft' | 'scheduled' | 'active' | 'paused' | 'completed'
  starts_at: string | null
  ends_at: string | null
  impressions: number
  conversions: number
  targeting: Record<string, unknown>
  creative_asset_ids: string[]
  revision: number
  updated_at: string
}

export interface ManagedAsset {
  id: string
  name: string
  asset_type: 'audio' | 'image' | 'knowledge' | 'firmware' | 'signature'
  storage_key: string
  sha256: string
  mime_type: string
  size_bytes: number
  status: 'staged' | 'uploading' | 'ready' | 'quarantined' | 'deleted'
  provider_id: string | null
  tags: string[]
  metadata?: Record<string, unknown>
  revision: number
  updated_at: string
}

const assetMimePolicy = {
  image: {
    extensions: { png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', webp: 'image/webp' },
    allowed: ['image/png', 'image/jpeg', 'image/webp'],
  },
  audio: {
    extensions: { wav: 'audio/wav', ima: 'audio/vnd.localike.ima-adpcm', adpcm: 'audio/vnd.localike.ima-adpcm' },
    allowed: ['audio/wav', 'audio/x-wav', 'audio/vnd.localike.ima-adpcm'],
  },
  knowledge: {
    extensions: { txt: 'text/plain', csv: 'text/csv', jsonl: 'application/x-ndjson', ndjson: 'application/x-ndjson' },
    allowed: ['text/plain', 'text/csv', 'application/x-ndjson'],
  },
} as const

export function controlledAssetMimeType(file: File, assetType: ManagedAsset['asset_type']): string {
  if (assetType === 'firmware') return 'application/octet-stream'
  if (assetType === 'signature') return 'application/vnd.localike.ed25519-signature'
  const policy = assetMimePolicy[assetType]
  const extension = file.name.toLowerCase().split('.').at(-1) ?? ''
  const byExtension = policy.extensions[extension as keyof typeof policy.extensions]
  const browserMime = file.type.toLowerCase().split(';', 1)[0].trim()
  const mime = byExtension ?? (policy.allowed.some((item) => item === browserMime) ? browserMime : '')
  if (!mime) {
    throw new ApiError(
      '该文件类型没有受控的服务端完整解码器。',
      415,
      undefined,
      'unsupported_asset_policy',
      { asset_type: assetType },
    )
  }
  return mime
}

export interface ManagedSigningKey {
  id: string
  key_id: string
  name: string
  public_key_base64: string
  algorithm: 'ed25519'
  fingerprint_sha256: string
  hardware_revisions: string[]
  status: 'active' | 'revoked'
  revoked_at: string | null
  revision: number
  created_at?: string
  updated_at?: string
}

export interface ManagedProvider {
  id: string
  code: string
  name: string
  provider_type: 'llm' | 'asr' | 'tts'
  adapter: 'openai_compatible_llm' | 'funasr_ws' | 'qwen3_tts_http'
  endpoint: string
  health_url: string
  model: string | null
  secret_env_key: string | null
  enabled: boolean
  timeout_ms: number
  pricing: Record<string, number>
  config: Record<string, unknown>
  revision: number
  updated_at: string
}

export interface ManagedFirmware {
  id: string
  version: string
  artifact_asset_id: string
  signature_asset_id: string
  signing_key_id: string
  sha256: string
  size_bytes: number
  signature_sha256: string
  signing_key_fingerprint_sha256: string
  signature_algorithm: 'ed25519-sha256-digest-v1'
  verified_at: string
  hardware_revision: string
  status: 'ready' | 'revoked'
  release_notes: string
  revision: number
  created_at?: string
  updated_at?: string
}

export interface ManagedOtaRollout {
  id: string
  name: string
  firmware_id: string
  previous_firmware_id: string | null
  status: 'draft' | 'running' | 'paused' | 'completed' | 'rolling_back' | 'rolled_back' | 'failed'
  cohort: Record<string, unknown>
  percentage: number
  failure_threshold_percent: number
  targeted_devices: number
  applied_devices: number
  failed_devices: number
  fanout_status?: 'idle' | 'pending' | 'running' | 'paused' | 'completed'
  automatic_action?: 'none' | 'paused_threshold' | 'rollback_threshold'
  transition_generation?: number
  transition_action?: 'none' | 'start' | 'resume' | 'rollback'
  revision: number
  updated_at: string
}

export interface ManagedReview {
  id: string
  traveler: string
  route: string
  rating: number
  excerpt: string
  status: 'pending' | 'approved' | 'rejected'
  created_at: string
  updated_at?: string
  revision?: number
  moderation_reason?: string | null
}

interface PublicReview extends Omit<ManagedReview, 'route'> {
  route_name: string
  route_id: string
  user_id: string
}

export interface OtaCommandHistory {
  command_id_sha256: string
  rollout_id: string
  device_id: string
  generation: number
  firmware_id: string
  firmware_version: string
  firmware_sha256: string
  signature_asset_id: string
  signature_sha256: string
  signature_algorithm: string
  signing_key_resource_id: string
  signing_key_id: string
  signing_key_fingerprint_sha256: string
  command_status: 'queued' | 'sent' | 'received' | 'applied' | 'failed' | 'expired'
  runtime_generation: number
  issued_at: string
  expires_at: string
  acked_at: string | null
  ack_reason_code: string | null
  result_stage: string
  result_progress: number
  result_reason_code: string | null
  result_recorded_at: string
  source_event_id: string | null
  command_created_at: string
  command_updated_at: string
  projected_at: string
  command_pruned_at: string
}

export interface ManagedBudget extends ManagedAgentConfig {
  scope: string
  period: 'daily' | 'monthly'
  metric: 'amount_micros' | 'requests' | 'tokens' | 'characters' | 'seconds' | 'bytes'
  currency: string
  limit_micros: number | null
  limit_quantity: number | null
  warning_percent: number
  enabled: boolean
}

export interface BudgetReservation extends ManagedAgentConfig {
  subject_type: 'agent_run' | 'media_job'
  subject_id: string
  provider_code: string
  trip_id: string | null
  user_id: string | null
  currency: string
  status: 'reserved' | 'settled' | 'released' | 'unknown_outcome'
  expires_at: string
  provider_started_at: string | null
  unknown_at: string | null
  settled_at: string | null
  released_at: string | null
  reconciliation_reason: string | null
  allocations: Array<Record<string, unknown>>
}

export interface CostEvent extends ManagedAgentConfig {
  provider_id: string
  source: 'provider_meter' | 'server_pricing'
  amount_micros: number
  quantity: number
  unit: 'tokens' | 'characters' | 'seconds' | 'requests' | 'bytes'
  currency: string
  pricing_version: string
  metered_at: string
  provider_event_id: string
  run_id: string | null
  job_id: string | null
  trip_id: string | null
  user_id: string | null
}

export interface IdempotencyReceipt {
  namespace_hash: string
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  path: string
  request_hash: string
  subject_hash: string | null
  idempotency_key_hash: string | null
  state: 'pending' | 'completed' | 'abandoned'
  status_code: number | null
  created_at: string
  completed_at: string | null
}

export interface RateLimitWindow {
  key_hash: string
  policy: string
  subject_hash: string
  window_started_at: string
  count: number
  limit: number
  window_seconds: number
  updated_at: string
}

export interface AuditRecord {
  id: string
  actor: string
  action: string
  resource: string
  result: 'success' | 'warning' | 'failure'
  request_id: string | null
  details: Record<string, unknown>
  created_at: string
}

export interface OutboxRecord {
  id: string
  topic: string
  aggregate_type: string
  aggregate_id: string
  attempts: number
  state: 'pending' | 'processed' | 'failed'
  available_at: string
  processed_at: string | null
  failed_at: string | null
  last_error: string | null
  dead_reason: string | null
  created_at: string
}

interface RawAchievement {
  id: string
  code: string
  name: string
  description: string
  points: number
  criteria: AchievementSummary['criteria']
  enabled: boolean
  revision: number
}

interface RawGovernanceHealth {
  status?: string
  environment?: string
  checks?: Record<string, unknown>
  workload?: RawDashboard
  services?: Array<{ name: string; status: string; latency_ms: number; detail: string }>
}

function numberValue(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0
}

function mapAdmin(user: AdminUser): AdminUser {
  const allowed: Role[] = ['superadmin', 'content_editor', 'operator', 'ai_engineer', 'observer']
  return {
    ...user,
    roles: Array.isArray(user.roles) ? user.roles.filter((role): role is Role => allowed.includes(role)) : [],
    active: user.active !== false,
  }
}

function mapRoute(route: RawRoute): RouteSummary {
  return {
    id: route.id,
    name: route.name,
    city: route.city,
    version: route.revision,
    pois: route.waypoint_ids.length,
    pointIds: route.waypoint_ids,
    hiddenPointIds: route.hidden_waypoint_ids ?? [],
    soundEffectIds: route.sound_effect_ids ?? [],
    status: route.status,
    updatedAt: route.updated_at,
  }
}

function mapPoint(point: RawPoi, _index: number, _total: number, _previous?: ContentPoint): ContentPoint {
  const metadata = point.metadata ?? {}
  const hasEditorOverride = typeof metadata.editor_x === 'number' && Number.isFinite(metadata.editor_x)
    && typeof metadata.editor_y === 'number' && Number.isFinite(metadata.editor_y)
    && metadata.editor_x >= 0 && metadata.editor_x <= 100
    && metadata.editor_y >= 0 && metadata.editor_y <= 100
  return {
    id: point.id,
    x: hasEditorOverride ? metadata.editor_x as number : Number.NaN,
    y: hasEditorOverride ? metadata.editor_y as number : Number.NaN,
    layoutSource: hasEditorOverride ? 'editor_override' : undefined,
    name: point.name,
    kind: typeof metadata.kind === 'string' ? metadata.kind : '讲解点',
    status: point.status === 'published' ? 'ready' : point.status === 'archived' ? 'review' : 'draft',
    revision: point.revision,
    description: point.description,
    city: point.city,
    tags: point.tags ?? [],
    latitude: point.latitude,
    longitude: point.longitude,
    updatedAt: point.updated_at,
    metadata,
    entityKind: 'pois',
  }
}

function mapAttachedPoint(
  point: RawRhythmPoint | RawMusicPoint,
  pois: ContentPoint[],
  _index: number,
): ContentPoint {
  const anchor = point.poi_id ? pois.find((item) => item.id === point.poi_id) : undefined
  const metadata = point.metadata ?? {}
  const isMusic = 'asset_id' in point
  const hasEditorOverride = typeof metadata.editor_x === 'number' && Number.isFinite(metadata.editor_x)
    && typeof metadata.editor_y === 'number' && Number.isFinite(metadata.editor_y)
    && metadata.editor_x >= 0 && metadata.editor_x <= 100
    && metadata.editor_y >= 0 && metadata.editor_y <= 100
  return {
    id: point.id,
    x: hasEditorOverride ? metadata.editor_x as number : anchor?.x ?? Number.NaN,
    y: hasEditorOverride ? metadata.editor_y as number : anchor?.y ?? Number.NaN,
    layoutSource: hasEditorOverride ? 'editor_override' : anchor?.layoutSource,
    name: point.name,
    kind: isMusic ? '音乐点' : '节奏点',
    status: point.status === 'published' ? 'ready' : point.status === 'archived' ? 'review' : 'draft',
    revision: point.revision,
    description: isMusic ? `Asset ${point.asset_id}` : point.instruction,
    latitude: anchor?.latitude ?? 0,
    longitude: anchor?.longitude ?? 0,
    metadata,
    entityKind: isMusic ? 'music_points' : 'rhythm_points',
    routeId: point.route_id,
    poiId: point.poi_id,
    sequence: point.sequence,
    ...(isMusic ? {
      assetId: point.asset_id,
      offsetSeconds: point.offset_seconds,
      durationSeconds: point.duration_seconds,
    } : { dwellSeconds: point.dwell_seconds }),
  }
}

function mapAgentRun(run: RawAgentRun): AgentRun {
  const usage = run.usage ?? {}
  const input = run.input
  const context = input?.context
  const source = typeof input?.prompt === 'string' && context && typeof context === 'object'
    ? input as unknown as AgentRun['source']
    : undefined
  return {
    id: run.id,
    tripId: run.trip_id ?? '未绑定行程',
    guide: run.purpose || run.provider,
    model: run.model,
    status: run.status === 'completed' ? 'completed' : run.status === 'queued' || run.status === 'running' ? 'running' : 'failed',
    latencyMs: numberValue(usage.latency_ms ?? usage.total_latency_ms),
    tokens: numberValue(usage.total_tokens ?? usage.tokens),
    cost: numberValue(usage.cost_cny ?? usage.cost),
    startedAt: run.created_at,
    source,
  }
}

function mapAgentEvent(event: RawAgentEvent): TraceEvent {
  const category = event.kind.split('.')[0]
  const tone: TraceEvent['tone'] = category === 'tool'
    ? 'amber'
    : category === 'stream' || category === 'turn'
      ? 'blue'
      : event.kind.includes('failed') || event.kind.includes('unknown')
        ? 'rose'
        : event.kind.includes('completed')
          ? 'teal'
          : 'violet'
  return {
    cursor: event.cursor,
    time: new Intl.DateTimeFormat('zh-CN', { hour: '2-digit', minute: '2-digit', second: '2-digit', fractionalSecondDigits: 3, hour12: false }).format(new Date(event.created_at)),
    type: event.kind,
    title: event.kind.replaceAll('.', ' / '),
    detail: Object.keys(event.data).length ? JSON.stringify(event.data, null, 2) : '无附加数据',
    tone,
  }
}

function subscribeAgentEvents(runId: string, after: number, handlers: AgentEventStreamHandlers): () => void {
  const controller = new AbortController()
  let cursor = after

  const open = async (): Promise<Response> => fetch(
    `${API_BASE}/agent/runs/${encodeURIComponent(runId)}/stream?after=${cursor}`,
    {
      method: 'GET',
      credentials: 'include',
      cache: 'no-store',
      headers: { Accept: 'text/event-stream', 'X-Request-ID': randomId() },
      signal: controller.signal,
    },
  )

  void (async () => {
    try {
      let response = await open()
      if (response.status === 401 && await refreshAdminSession()) response = await open()
      if (response.status === 401) window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT))
      if (!response.ok) {
        const payload = await response.json().catch(() => null) as ErrorPayload | null
        if (!payload?.error || typeof payload.error.code !== 'string' || typeof payload.error.message !== 'string' || typeof payload.error.request_id !== 'string') {
          throw new ApiError('Trace 错误响应不符合 Localike 2.0 envelope 契约。', response.status, response.headers.get('x-request-id') ?? undefined, 'invalid_error_contract')
        }
        throw new ApiError(
          payload.error.message,
          response.status,
          payload.error.request_id,
          payload.error.code,
          payload.error.details,
        )
      }
      if (!response.headers.get('content-type')?.toLowerCase().startsWith('text/event-stream')) throw new ApiError('Trace 流响应不是 text/event-stream。', 502, response.headers.get('x-request-id') ?? undefined, 'invalid_stream_contract')
      if (!response.body) throw new ApiError('Trace 流响应缺少可读数据。', 0, undefined, 'stream_body_missing')

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      let terminal = false
      while (!controller.signal.aborted) {
        const { done, value } = await reader.read()
        buffer += decoder.decode(value, { stream: !done }).replaceAll('\r\n', '\n')
        let boundary = buffer.indexOf('\n\n')
        while (boundary >= 0) {
          const frame = buffer.slice(0, boundary)
          buffer = buffer.slice(boundary + 2)
          const eventName = frame.split('\n').find((line) => line.startsWith('event:'))?.slice(6).trim() ?? 'message'
          const eventId = Number(frame.split('\n').find((line) => line.startsWith('id:'))?.slice(3).trim() ?? cursor)
          const dataText = frame.split('\n').filter((line) => line.startsWith('data:')).map((line) => line.slice(5).trimStart()).join('\n')
          if (Number.isFinite(eventId)) cursor = eventId
          if (eventName === 'done' || eventName === 'timeout') {
            terminal = true
            handlers.onDone(eventName, cursor)
            await reader.cancel()
            return
          }
          if (dataText) {
            const raw = JSON.parse(dataText) as RawAgentEvent
            cursor = raw.cursor
            handlers.onEvent(mapAgentEvent(raw))
          }
          boundary = buffer.indexOf('\n\n')
        }
        if (done) break
      }
      if (!controller.signal.aborted && !terminal) handlers.onDone('closed', cursor)
    } catch (error) {
      if (controller.signal.aborted) return
      handlers.onError(error instanceof ApiError
        ? error
        : new ApiError('Trace 流已断开，可按最后 cursor 重新连接。', 0, undefined, 'stream_disconnected', error))
    }
  })()

  return () => controller.abort()
}

function mapDevice(device: RawDevice): DeviceSummary {
  const capabilities = device.capabilities ?? {}
  const rawBattery = capabilities.battery
  return {
    id: device.id,
    name: typeof capabilities.name === 'string' ? capabilities.name : device.serial_number ?? device.id,
    deviceClass: capabilities.device_class === 'hardware' || capabilities.device_class === 'simulator' ? capabilities.device_class : 'restricted',
    firmware: device.firmware_version ?? null,
    network: typeof capabilities.network === 'string' ? capabilities.network : null,
    battery: typeof rawBattery === 'number' ? rawBattery : null,
    status: device.status === 'online' ? 'online' : device.status === 'offline' || device.status === 'revoked' ? 'offline' : 'attention',
    lastSeen: device.last_seen_at ?? device.updated_at ?? null,
    runtimeGeneration: device.runtime_generation ?? null,
    connectionGeneration: device.connection_generation ?? null,
    configRevision: device.config_revision ?? null,
    capabilities,
    ownerUserId: device.owner_user_id ?? null,
  }
}

function mapTrip(trip: RawTrip, routeById: Map<string, RawRoute>, deviceRows: RawDevice[]): LiveTrip {
  const metadata = trip.metadata ?? {}
  const device = deviceRows.find((item) => item.id === trip.device_id) ?? deviceRows.find((item) => item.owner_user_id === trip.user_id)
  const progress = numberValue(metadata.progress_percent)
  return {
    id: trip.id,
    traveler: trip.user_id,
    route: trip.route_id ? routeById.get(trip.route_id)?.name ?? trip.route_id : '自由探索',
    mode: trip.mode,
    status: trip.status === 'active' ? 'active' : trip.status === 'paused' ? 'paused' : 'ended',
    progress: Math.max(0, Math.min(100, progress)),
    lastSeen: trip.updated_at,
    device: device?.serial_number ?? trip.device_id ?? '未绑定设备',
    deviceId: trip.device_id ?? device?.id,
    runtimeGeneration: trip.runtime_generation ?? 0,
    planRevision: trip.plans?.at(-1)?.revision ?? null,
    createdAt: trip.created_at ?? trip.updated_at,
  }
}

function healthServices(payload: RawGovernanceHealth): DashboardService[] {
  if (payload.services) return payload.services
  return Object.entries(payload.checks ?? {}).map(([name, value]) => ({
    name: name === 'database' ? 'Database' : name === 'postgis' ? 'PostGIS' : name,
    status: value === 'ok' || value === 'not_applicable' ? 'healthy' : 'degraded',
    latency_ms: 0,
    detail: String(value),
  }))
}

export interface DashboardService {
  name: string
  status: string
  latency_ms: number
  detail: string
}

export interface DashboardData {
  metrics: DashboardMetric[]
  live_trips: LiveTrip[]
  agent_runs: AgentRun[]
  queue_depth: number
  provider_health: number
  services: DashboardService[]
  windows: {
    trips: { applied_limit: number; total: number; has_more: boolean }
    agent_runs: { applied_limit: number; total: number; has_more: boolean }
    devices: { applied_limit: number; total: number; has_more: boolean }
  }
}

export interface AgentStudioConfig {
  guides: RawGuide[]
  prompts: RawPrompt[]
  triggers: RawTrigger[]
}

export interface DashboardAccess {
  trips: boolean
  devices: boolean
  agentRuns: boolean
  health: boolean
}

export type GlobalSearchGroup = 'content' | 'trips' | 'users' | 'devices'

export interface GlobalSearchScopes {
  content: boolean
  people: boolean
  devices: boolean
}

export interface GlobalSearchResult {
  key: string
  group: GlobalSearchGroup
  title: string
  description: string
  to: { path: string; query: Record<string, string> }
}

function matchesSearchTerm(values: unknown[], term: string): boolean {
  const normalized = term.trim().toLocaleLowerCase('zh-CN')
  return values.some((value) => String(value ?? '').toLocaleLowerCase('zh-CN').includes(normalized))
}

async function globalSearch(term: string, scopes: GlobalSearchScopes): Promise<GlobalSearchResult[]> {
  // 全局搜索也必须遵循集合端点的 typed sort；匹配度不是当前
  // PageQuery 公开的排序值，不向服务端发送自造的 relevance。
  const query: CollectionQuery = { page: 1, pageSize: 8, sort: 'updated_at_desc', filter: term }
  const [routesPage, poisPage, tripsPage, usersPage, devicesPage] = await Promise.all([
    scopes.content
      ? requestCollection<RawRoute>('/content/routes', query, async () => (preview.get<RouteSummary[]>('routes')).map((route) => ({
          id: route.id, name: route.name, city: route.city, revision: route.version, waypoint_ids: route.pointIds,
          hidden_waypoint_ids: route.hiddenPointIds, sound_effect_ids: route.soundEffectIds, status: route.status, updated_at: route.updatedAt,
        })))
      : Promise.resolve(null),
    scopes.content
      ? requestCollection<RawPoi>('/content/pois', query, async () => (preview.get<ContentPoint[]>('points')).map((point) => ({
          id: point.id, name: point.name, description: point.description, latitude: point.latitude, longitude: point.longitude,
          revision: point.revision, status: point.status === 'ready' ? 'published' : point.status === 'review' ? 'archived' : 'draft', metadata: point.metadata,
        })))
      : Promise.resolve(null),
    scopes.people ? requestCollection<RawTrip>('/trips', query) : Promise.resolve(null),
    scopes.people ? requestCollection<RawManagedUser>('/users', query, async () => (preview.get<UserSummary[]>('users')).map((user) => ({
      id: user.id, name: user.name, status: user.status, locale: user.locale, tags: user.tags, revision: user.revision, updated_at: user.updatedAt,
    }))) : Promise.resolve(null),
    scopes.devices ? requestCollection<RawDevice>('/devices', query, async () => (preview.get<DeviceSummary[]>('devices')).map((device) => ({
      id: device.id, serial_number: device.name, status: device.status === 'attention' ? 'offline' : device.status,
      owner_user_id: device.ownerUserId, capabilities: device.capabilities, firmware_version: device.firmware,
      runtime_generation: device.runtimeGeneration ?? undefined, connection_generation: device.connectionGeneration ?? undefined,
      config_revision: device.configRevision ?? undefined, last_seen_at: device.lastSeen,
    }))) : Promise.resolve(null),
  ])

  // 对 typed filter 的返回再做确定性收窄，避免不同字段的模糊匹配污染分组；
  // 客户端只删减服务端事实，不补造搜索结果。
  const routes = (routesPage?.items ?? []).filter((route) => matchesSearchTerm([route.id, route.name, route.city], term)).slice(0, 5)
  const pois = (poisPage?.items ?? []).filter((poi) => matchesSearchTerm([poi.id, poi.name, poi.description], term)).slice(0, 5)
  const trips = (tripsPage?.items ?? []).filter((trip) => matchesSearchTerm([trip.id, trip.user_id, trip.route_id, trip.device_id, trip.mode, trip.status], term)).slice(0, 5)
  const users = (usersPage?.items ?? []).filter((user) => matchesSearchTerm([user.id, user.name, user.locale, ...(user.tags ?? [])], term)).slice(0, 5)
  const devices = (devicesPage?.items ?? []).filter((device) => matchesSearchTerm([device.id, device.serial_number, device.firmware_version, device.status], term)).slice(0, 5)

  return [
    ...routes.map((route): GlobalSearchResult => ({
      key: `route:${route.id}`, group: 'content', title: route.name, description: `路线 · ${route.city} · v${route.revision}`,
      to: { path: '/content', query: { workspace: 'routes', filter: term, selected: route.id } },
    })),
    ...pois.map((poi): GlobalSearchResult => ({
      key: `poi:${poi.id}`, group: 'content', title: poi.name, description: `POI · ${poi.city ?? '未设城市'} · ${poi.id.slice(0, 8)}`,
      to: { path: '/content', query: { workspace: 'pois', filter: term, point: poi.id } },
    })),
    ...trips.map((trip): GlobalSearchResult => ({
      key: `trip:${trip.id}`, group: 'trips', title: `行程 ${trip.id.slice(0, 8)}`, description: `${trip.mode} · ${trip.status}`,
      to: { path: '/people', query: { tab: 'trips', filter: term, selected: trip.id } },
    })),
    ...users.map((user): GlobalSearchResult => ({
      key: `user:${user.id}`, group: 'users', title: user.name, description: `用户 · ${user.status} · ${user.id.slice(0, 8)}`,
      to: { path: '/people', query: { tab: 'users', filter: term, selected: user.id } },
    })),
    ...devices.map((device): GlobalSearchResult => ({
      key: `device:${device.id}`, group: 'devices', title: device.serial_number || device.id, description: `设备 · ${device.status} · ${device.firmware_version ?? '未报告固件'}`,
      to: { path: '/people', query: { tab: 'devices', filter: term, selected: device.id } },
    })),
  ]
}

async function dashboard(access: DashboardAccess): Promise<DashboardData> {
  if (preview.enabled) {
    const result = await request<DashboardData>('/admin/dashboard', {}, () => preview.get<DashboardData>('dashboard'))
    return result.windows ? result : {
      ...result,
      windows: {
        trips: { applied_limit: result.live_trips.length || 6, total: result.live_trips.length, has_more: false },
        agent_runs: { applied_limit: result.agent_runs.length || 6, total: result.agent_runs.length, has_more: false },
        devices: { applied_limit: 100, total: 0, has_more: false },
      },
    }
  }

  const tripQuery: CollectionQuery = { page: 1, pageSize: 6, sort: 'updated_at_desc' }
  const runQuery: CollectionQuery = { page: 1, pageSize: 6, sort: 'updated_at_desc' }
  const deviceQuery: CollectionQuery = { page: 1, pageSize: 100, sort: 'updated_at_desc' }
  const [summary, tripPage, runPage, devicePage, health] = await Promise.all([
    request<RawDashboard>('/admin/dashboard'),
    access.trips ? requestCollection<RawTrip>('/trips', tripQuery) : Promise.resolve(null),
    access.agentRuns ? requestCollection<RawAgentRun>('/agent/runs', runQuery) : Promise.resolve(null),
    access.devices || access.trips ? requestCollection<RawDevice>('/devices', deviceQuery) : Promise.resolve(null),
    access.health ? request<RawGovernanceHealth>('/governance/health') : Promise.resolve({ services: [] }),
  ])
  const tripRows = tripPage?.items ?? []
  const runRows = runPage?.items ?? []
  const deviceRows = devicePage?.items ?? []
  // 行程页不为了显示路线名额外穿透内容域；无内容权限时以稳定 route_id 展示。
  const routeById = new Map<string, RawRoute>()
  return {
    metrics: [
      { label: '进行中行程', value: summary.active_trips, delta: null, tone: 'amber' },
      { label: '在线设备', value: summary.online_devices, delta: null, tone: 'teal' },
      { label: 'Agent Runs', value: summary.agent_runs, delta: null, tone: 'blue' },
      { label: '待处理 Outbox', value: summary.pending_outbox, delta: null, tone: 'rose' },
    ],
    live_trips: tripRows.map((trip) => mapTrip(trip, routeById, deviceRows)),
    agent_runs: runRows.map(mapAgentRun),
    queue_depth: summary.pending_outbox,
    provider_health: (() => {
      const services = healthServices(health)
      return services.length ? Math.round(services.filter((item) => item.status === 'healthy').length * 10_000 / services.length) / 100 : 0
    })(),
    services: healthServices(health),
    windows: {
      trips: { applied_limit: tripQuery.pageSize, total: tripPage?.total ?? 0, has_more: tripPage?.hasNext ?? false },
      agent_runs: { applied_limit: runQuery.pageSize, total: runPage?.total ?? 0, has_more: runPage?.hasNext ?? false },
      devices: { applied_limit: deviceQuery.pageSize, total: devicePage?.total ?? 0, has_more: devicePage?.hasNext ?? false },
    },
  }
}

export const api = {
  auth: {
    session: async () => mapAdmin(await request<AdminUser>('/admin/me', { suppressAuthExpiredEvent: true }, () => preview.get<AdminUser>('session'))),
    login: async (email: string, password: string) => mapAdmin(await request<AdminUser>(
      '/auth/admin/login',
      { method: 'POST', body: { email, password }, skipAuthRefresh: true },
      () => preview.get<AdminUser>('login', { email }),
    )),
    logout: () => request<void>('/auth/admin/logout', { method: 'POST', skipAuthRefresh: true }, () => undefined),
  },
  dashboard,
  globalSearch,
  content: {
    page: <T>(kind: string, query: CollectionQuery, status?: string) => requestCollection<T>(
      `/content/${encodeURIComponent(kind)}${status && status !== 'all' ? `?status=${encodeURIComponent(status)}` : ''}`,
      query,
      () => preview.get<T[]>('editorialRecords', { kind }),
    ),
    get: <T>(kind: string, entityId: string) => request<T>(
      `/content/${encodeURIComponent(kind)}/${encodeURIComponent(entityId)}`,
    ),
    create: <T>(kind: string, payload: Record<string, unknown>) => request<T>(
      `/content/${encodeURIComponent(kind)}`,
      { method: 'POST', body: payload },
    ),
    update: <T>(kind: string, entityId: string, expectedRevision: number, changes: Record<string, unknown>) => request<T>(
      `/content/${encodeURIComponent(kind)}/${encodeURIComponent(entityId)}`,
      { method: 'PATCH', body: { expected_revision: expectedRevision, changes } },
    ),
    publishEntity: <T>(kind: string, entityId: string, expectedRevision: number, evaluationRunId?: string) => request<T>(
      `/content/${encodeURIComponent(kind)}/${encodeURIComponent(entityId)}/publish`,
      { method: 'POST', body: { expected_revision: expectedRevision, ...(evaluationRunId ? { evaluation_run_id: evaluationRunId } : {}) } },
    ),
    validate: (kind: string, entityId: string, expectedRevision: number) => request<ContentValidation>(
      `/content/${encodeURIComponent(kind)}/${encodeURIComponent(entityId)}/validate`,
      { method: 'POST', body: { expected_revision: expectedRevision } },
      () => preview.get<ContentValidation>('validateContent', { kind, entityId, revision: expectedRevision }),
    ),
    revisionsPage: (kind: string, entityId: string, query: CollectionQuery, revision?: number) => requestCollection<ContentRevision>(
      `/content/${encodeURIComponent(kind)}/${encodeURIComponent(entityId)}/revisions`,
      query,
      () => preview.get<ContentRevision[]>('contentRevisions', { kind, entityId, revision }),
    ),
    diff: (kind: string, entityId: string, fromRevision: number, toRevision: number) => request<ContentDiff>(
      `/content/${encodeURIComponent(kind)}/${encodeURIComponent(entityId)}/diff?from_revision=${fromRevision}&to_revision=${toRevision}`,
      {},
      () => preview.get<ContentDiff>('contentDiff', { kind, entityId, fromRevision, toRevision }),
    ),
    rollback: <T>(kind: string, entityId: string, expectedRevision: number, targetRevision: number, mockInput?: unknown) => request<T>(
      `/content/${encodeURIComponent(kind)}/${encodeURIComponent(entityId)}/rollback`,
      { method: 'POST', body: { expected_revision: expectedRevision, target_revision: targetRevision } },
      () => preview.get<T>('rollbackRoute', { route: mockInput, expectedRevision, targetRevision }),
    ),
    archive: <T>(kind: string, entityId: string, expectedRevision: number) => request<T>(
      `/content/${encodeURIComponent(kind)}/${encodeURIComponent(entityId)}?expected_revision=${expectedRevision}`,
      { method: 'DELETE' },
    ),
    routesPage: async (query: CollectionQuery, status?: string) => mapCollectionPage(
      await requestCollection<RawRoute>(
        `/content/routes${status && status !== 'all' ? `?status=${encodeURIComponent(status)}` : ''}`,
        query,
        async () => (preview.get<RouteSummary[]>('routes')).map((route) => ({
          id: route.id,
          name: route.name,
          city: route.city,
          revision: route.version,
          waypoint_ids: route.pointIds,
          hidden_waypoint_ids: route.hiddenPointIds,
          sound_effect_ids: route.soundEffectIds,
          status: route.status,
          updated_at: route.updatedAt,
        })),
      ),
      mapRoute,
    ),
    pointsPage: async (query: CollectionQuery, status?: string) => {
      const page = await requestCollection<RawPoi>(
        `/content/pois${status && status !== 'all' ? `?status=${encodeURIComponent(status)}` : ''}`,
        query,
        async () => (preview.get<ContentPoint[]>('points')).map((point) => ({
          id: point.id,
          name: point.name,
          city: point.city,
          description: point.description,
          latitude: point.latitude,
          longitude: point.longitude,
          revision: point.revision,
          status: point.status === 'ready' ? 'published' : point.status === 'review' ? 'archived' : 'draft',
          tags: point.tags,
          metadata: point.metadata,
          updated_at: point.updatedAt,
        })),
      )
      return mapCollectionPage(page, (point, index) => mapPoint(point, index, page.total))
    },
    mapAttachedPoint,
    previewKnowledgeImport: async (file: File, format: 'csv' | 'jsonl') => {
      const contentType = format === 'csv' ? 'text/csv' : 'application/x-ndjson'
      const previewResult = await request<KnowledgeImportPreview>(
        `/content/knowledge/imports/preview?format=${format}`,
        {
          method: 'POST',
          headers: { 'Content-Type': contentType },
          rawBody: file,
        },
      )
      const receipt = await request<KnowledgeImportReceipt>(
        `/content/knowledge/imports/${encodeURIComponent(previewResult.import_id)}`,
      )
      return { preview: previewResult, receipt }
    },
    getKnowledgeImport: (importId: string) => request<KnowledgeImportReceipt>(
      `/content/knowledge/imports/${encodeURIComponent(importId)}`,
    ),
    applyKnowledgeImport: (receipt: KnowledgeImportReceipt) => request<KnowledgeImportApplyResult>(
      `/content/knowledge/imports/${encodeURIComponent(receipt.id)}/apply`,
      { method: 'POST', body: { expected_revision: receipt.revision } },
    ),
    updatePoint: async (point: ContentPoint, changes: Record<string, unknown>) => {
      if (preview.enabled) {
        return request<ContentPoint>(
          `/content/pois/${encodeURIComponent(point.id)}`,
          { method: 'PATCH', body: { expected_revision: point.revision, changes } },
          () => preview.get<ContentPoint>('updatePoint', { point, changes }),
        )
      }
      const updated = await request<RawPoi>(
        `/content/pois/${encodeURIComponent(point.id)}`,
        { method: 'PATCH', body: { expected_revision: point.revision, changes } },
      )
      return mapPoint(updated, 0, 1, point)
    },
    publish: async (routeId: string, expectedRevision: number) => {
      if (preview.enabled) {
        return request<{ version: number }>(
          `/content/routes/${encodeURIComponent(routeId)}/publish`,
          { method: 'POST', body: { expected_revision: expectedRevision } },
          () => preview.get<{ version: number }>('publishRoute', { expectedRevision }),
        )
      }
      const published = await request<RawRoute>(
        `/content/routes/${encodeURIComponent(routeId)}/publish`,
        { method: 'POST', body: { expected_revision: expectedRevision } },
      )
      return { version: published.revision }
    },
    rollbackRoute: async (route: RouteSummary, targetRevision: number) => {
      if (preview.enabled) {
        return request<RouteSummary>(
          `/content/routes/${encodeURIComponent(route.id)}/rollback`,
          { method: 'POST', body: { expected_revision: route.version, target_revision: targetRevision } },
          () => ({ ...route, version: route.version + 1, status: 'draft', updatedAt: new Date().toISOString() }),
        )
      }
      return mapRoute(await request<RawRoute>(
        `/content/routes/${encodeURIComponent(route.id)}/rollback`,
        { method: 'POST', body: { expected_revision: route.version, target_revision: targetRevision } },
      ))
    },
    updateRouteRelations: async (
      route: RouteSummary,
      waypointIds: string[],
      hiddenWaypointIds: string[],
      soundEffectIds = route.soundEffectIds,
      identity?: { name: string; city: string },
    ) => mapRoute(await request<RawRoute>(
      `/content/routes/${encodeURIComponent(route.id)}`,
      {
        method: 'PATCH',
        body: {
          expected_revision: route.version,
          changes: {
            ...(identity ? { name: identity.name, city: identity.city } : {}),
            waypoint_ids: waypointIds,
            hidden_waypoint_ids: hiddenWaypointIds,
            sound_effect_ids: soundEffectIds,
          },
        },
      },
    )),
  },
  trips: {
    page: async (query: CollectionQuery) => mapCollectionPage(
      await requestCollection<RawTrip>('/trips', query, async () => preview.get<LiveTrip[]>('trips').map((trip) => ({
        id: trip.id,
        user_id: trip.traveler,
        mode: trip.mode,
        status: trip.status === 'attention' ? 'active' : trip.status,
        route_id: null,
        device_id: trip.deviceId,
        runtime_generation: trip.runtimeGeneration,
        plans: trip.planRevision ? [{ revision: trip.planRevision }] : [],
        created_at: trip.createdAt,
        updated_at: trip.lastSeen,
      }))),
      (trip) => mapTrip(trip, new Map(), []),
    ),
    timeline: (tripId: string) => request<TripTimeline>(
      `/trips/${encodeURIComponent(tripId)}/timeline`,
      {},
      () => preview.get<TripTimeline>('tripTimeline', { tripId }),
    ),
    timelineStream: <T>(tripId: string, stream: 'events' | 'locations' | 'checkins' | 'messages', query: CursorCollectionQuery) => requestCursorCollection<T>(
      `/trips/${encodeURIComponent(tripId)}/timeline?stream=${stream}`,
      query,
    ),
  },
  users: {
    page: async (query: CollectionQuery) => mapCollectionPage(
      await requestCollection<RawManagedUser>('/users', query, async () => (preview.get<UserSummary[]>('users')).map((user) => ({
        id: user.id,
        name: user.name,
        status: user.status,
        locale: user.locale,
        tags: user.tags,
        revision: user.revision,
        updated_at: user.updatedAt,
      }))),
      (user) => ({
        id: user.id,
        name: user.name,
        status: user.status,
        locale: user.locale,
        tags: user.tags ?? [],
        revision: user.revision,
        updatedAt: user.updated_at,
      }),
    ),
    create: (payload: { name: string; locale: string; tags: string[]; notes?: string }) => request<RawManagedUser>(
      '/users',
      { method: 'POST', body: { ...payload, status: 'active', metadata: {} } },
    ),
    setStatus: (user: UserSummary, status: UserSummary['status']) => request<RawManagedUser>(
      `/users/${encodeURIComponent(user.id)}/status`,
      { method: 'POST', body: { expected_revision: user.revision, status } },
    ),
  },
  agent: {
    runsPage: async (query: CollectionQuery) => mapCollectionPage(
      await requestCollection<RawAgentRun>('/agent/runs', query, async () => preview.get<AgentRun[]>('agentRuns').map((run) => ({
        id: run.id,
        trip_id: run.tripId === '未绑定行程' ? null : run.tripId,
        purpose: run.guide,
        status: run.status === 'running' ? 'running' : run.status,
        provider: run.guide,
        model: run.model,
        input: run.source as unknown as Record<string, unknown>,
        usage: { latency_ms: run.latencyMs, total_tokens: run.tokens, cost_cny: run.cost },
        created_at: run.startedAt,
        updated_at: run.startedAt,
      }))),
      mapAgentRun,
    ),
    tools: () => request<AgentToolContract[]>('/agent/tools', {}, () => preview.get('agentTools')),
    managedConfigsPage: <T extends ManagedAgentConfig = ManagedAgentConfig>(kind: ManagedAgentConfigKind, query: CollectionQuery, status?: string) => requestCollection<T>(
      `/agent/config/${encodeURIComponent(kind)}${status ? `?status=${encodeURIComponent(status)}` : ''}`,
      query,
      () => preview.get<T[]>('managedAgentConfigs', { kind }),
    ),
    createManagedConfig: (kind: ManagedAgentConfigKind, data: Record<string, unknown>, externalKey?: string) => request<ManagedAgentConfig>(
      `/agent/config/${encodeURIComponent(kind)}`,
      { method: 'POST', body: { ...(externalKey ? { external_key: externalKey } : {}), data } },
      () => preview.get('saveManagedAgentConfig', { kind, data, externalKey }),
    ),
    updateManagedConfig: (item: ManagedAgentConfig, changes: Record<string, unknown>) => request<ManagedAgentConfig>(
      `/agent/config/${encodeURIComponent(item.kind)}/${encodeURIComponent(item.id)}`,
      { method: 'PATCH', body: { expected_revision: item.revision, changes } },
      () => preview.get('saveManagedAgentConfig', { kind: item.kind, id: item.id, revision: item.revision, data: { ...item, ...changes }, externalKey: item.external_key }),
    ),
    transitionManagedConfig: <T extends ManagedAgentConfig = ManagedAgentConfig>(item: T, action: 'publish' | 'archive', reviewReason?: string) => request<T>(
      `/agent/config/${encodeURIComponent(item.kind)}/${encodeURIComponent(item.id)}/${action}`,
      { method: 'POST', body: { expected_revision: item.revision, ...(reviewReason ? { review_reason: reviewReason } : {}) } },
      () => preview.get('transitionManagedAgentConfig', { item, action }),
    ),
    eventsPage: async (runId: string, query: CursorCollectionQuery) => mapCursorPage(
      await requestCursorCollection<RawAgentEvent>(
        `/agent/runs/${encodeURIComponent(runId)}/events`,
        query,
        () => preview.get<RawAgentEvent[]>('agentEvents', { runId }),
        'after',
      ),
      mapAgentEvent,
    ),
    evaluationEvidence: async (runId: string) => normalizePromptEvaluationEvidence(
      await request<unknown>(`/agent/runs/${encodeURIComponent(runId)}/evaluation-evidence`),
    ),
    subscribeEvents: subscribeAgentEvents,
    create: async (source: AgentRunSource) => {
      if (preview.enabled) {
        return request<AgentRun>('/agent/runs', { method: 'POST', body: source }, () => preview.get<AgentRun>('createAgentRun', { source }))
      }
      return mapAgentRun(await request<RawAgentRun>('/agent/runs', { method: 'POST', body: source }))
    },
    replay: async (runId: string) => {
      const result = await request<{ run: RawAgentRun; replay_of: string; disposition: 'accepted' }>(
        `/agent/runs/${encodeURIComponent(runId)}/replay`,
        { method: 'POST' },
        () => preview.get<{ run: RawAgentRun; replay_of: string; disposition: 'accepted' }>('replayAgentRun', { runId }),
      )
      return { run_id: result.run.id, replay_of: result.replay_of, disposition: result.disposition }
    },
  },
  devices: {
    page: async (query: CollectionQuery) => mapCollectionPage(
      await requestCollection<RawDevice>('/devices', query, async () => preview.get<DeviceSummary[]>('devices').map((device) => ({
        id: device.id,
        serial_number: device.name,
        status: device.status === 'attention' ? 'offline' : device.status,
        owner_user_id: device.ownerUserId,
        capabilities: device.capabilities,
        firmware_version: device.firmware,
        runtime_generation: device.runtimeGeneration ?? undefined,
        connection_generation: device.connectionGeneration ?? undefined,
        config_revision: device.configRevision ?? undefined,
        last_seen_at: device.lastSeen,
      }))),
      mapDevice,
    ),
    eventsPage: (deviceId: string, query: CursorCollectionQuery, eventType = '') => requestCursorCollection<DeviceEventRecord>(
      `/devices/${encodeURIComponent(deviceId)}/events${eventType ? `?event_type=${encodeURIComponent(eventType)}` : ''}`,
      query,
      () => preview.get('deviceEvents', { deviceId, eventType, afterCursor: query.cursor ?? '' }),
    ),
    command: (deviceId: string, command: string, payload: Record<string, unknown>) => request<{ command_id: string }>(
      `/devices/${encodeURIComponent(deviceId)}/commands`,
      { method: 'POST', body: { command, payload } },
      () => preview.get<{ command_id: string }>('deviceCommand', { deviceId, command, payload }),
    ),
    pair: async (deviceId: string, userId: string) => mapDevice(await request<RawDevice>(
      `/devices/${encodeURIComponent(deviceId)}/pair`,
      { method: 'POST', body: { user_id: userId } },
    )),
    unpair: async (deviceId: string, expectedUserId?: string) => mapDevice(await request<RawDevice>(
      `/devices/${encodeURIComponent(deviceId)}/unpair`,
      { method: 'POST', body: { expected_user_id: expectedUserId || null } },
    )),
    revokeSessions: async (deviceId: string) => mapDevice(await request<RawDevice>(
      `/devices/${encodeURIComponent(deviceId)}/sessions/revoke`,
      { method: 'POST' },
    )),
  },
  operations: {
    campaignsPage: (query: CollectionQuery) => requestCollection<ManagedCampaign>(
      '/operations/campaigns', query, () => preview.get('campaignRecords'),
    ),
    createCampaign: (payload: Omit<ManagedCampaign, 'id' | 'revision' | 'updated_at'>) => request<ManagedCampaign>(
      '/operations/campaigns',
      { method: 'POST', body: payload },
    ),
    updateCampaign: (campaign: ManagedCampaign, changes: Record<string, unknown>) => request<ManagedCampaign>(
      `/operations/campaigns/${encodeURIComponent(campaign.id)}`,
      { method: 'PATCH', body: { expected_revision: campaign.revision, changes } },
    ),
    assetsPage: (query: CollectionQuery, filters?: { assetType?: ManagedAsset['asset_type']; status?: ManagedAsset['status'] }) => {
      const params = new URLSearchParams()
      if (filters?.assetType) params.set('asset_type', filters.assetType)
      if (filters?.status) params.set('status', filters.status)
      return requestCollection<ManagedAsset>(
        `/operations/assets${params.size ? `?${params}` : ''}`, query, () => preview.get('assets'),
      )
    },
    asset: (assetId: string) => request<ManagedAsset>(
      `/operations/assets/${encodeURIComponent(assetId)}`,
    ),
    deleteAsset: (asset: ManagedAsset) => request<ManagedAsset & { deletion_status: 'queued' }>(
      `/operations/assets/${encodeURIComponent(asset.id)}?expected_revision=${asset.revision}`,
      { method: 'DELETE' },
    ),
    uploadAsset: async (file: File, assetType: ManagedAsset['asset_type'], tags: string[]): Promise<ManagedAsset> => {
      const mimeType = controlledAssetMimeType(file, assetType)
      const sha256 = await sha256File(file)
      const receipt = await request<ManagedAsset & { upload_path: string }>('/operations/assets/upload-receipts', {
        method: 'POST',
        body: {
          name: file.name,
          asset_type: assetType,
          sha256,
          mime_type: mimeType,
          size_bytes: file.size,
          tags,
        },
      })
      return request<ManagedAsset>(`/operations/assets/${encodeURIComponent(receipt.id)}/content`, {
        method: 'PUT',
        headers: { 'Content-Type': mimeType },
        rawBody: file,
      })
    },
    achievementsPage: async (query: CollectionQuery) => mapCollectionPage(
      await requestCollection<RawAchievement>('/operations/achievements', query, () => preview.get<RawAchievement[]>('achievements')),
      (item) => ({
        id: item.id,
        code: item.code,
        name: item.name,
        description: item.description,
        points: item.points,
        criteria: item.criteria,
        enabled: item.enabled,
        revision: item.revision,
      }),
    ),
    createAchievement: (payload: { code: string; name: string; description: string; points: number; enabled: boolean; criteria: AchievementSummary['criteria'] }) => request<RawAchievement>(
      '/operations/achievements',
      { method: 'POST', body: payload },
    ),
    updateAchievement: (achievement: AchievementSummary, changes: Record<string, unknown>) => request<RawAchievement>(
      `/operations/achievements/${encodeURIComponent(achievement.id)}`,
      { method: 'PATCH', body: { expected_revision: achievement.revision, changes } },
    ),
    overview: async () => {
      const result = await requestSummary<{
      campaigns: Array<{ id: string; title: string; channel: string; status: string; impressions: number; conversion: number }>
      reviews: Array<{ id: string; traveler: string; route: string; rating: number; excerpt: string; status: string; created_at: string }>
      assets: Array<{ label: string; count: number; usage: number; tone: string }>
      }, { campaigns: SummaryCollectionMeta; reviews: SummaryCollectionMeta; assets: SummaryCollectionMeta }>(
        '/operations/overview',
        ['campaigns', 'reviews', 'assets'],
        () => preview.get('operationsOverview'),
      )
      return { ...result.data, meta: result.meta }
    },
    reviewsPage: async (query: CollectionQuery, status?: string) => mapCollectionPage(
      await requestCollection<PublicReview>(
        `/operations/reviews${status && status !== 'all' ? `?status=${encodeURIComponent(status)}` : ''}`,
        query,
      ),
      (review): ManagedReview => ({
        id: review.id,
        traveler: review.traveler,
        route: review.route_name,
        rating: review.rating,
        excerpt: review.excerpt,
        status: review.status,
        created_at: review.created_at,
        updated_at: review.updated_at,
        revision: review.revision,
        moderation_reason: review.moderation_reason,
      }),
    ),
    moderateReview: (reviewId: string, decision: 'approve' | 'reject', reason?: string) => request<void>(
      `/operations/reviews/${encodeURIComponent(reviewId)}/moderation`,
      { method: 'POST', body: { decision, ...(reason ? { reason } : {}) } },
      () => preview.get<void>('moderateReview', { reviewId, decision, reason }),
    ),
  },
  governance: {
    adminsPage: async (query: CollectionQuery) => mapCollectionPage(
      await requestCollection<AdminUser>('/governance/admins', query, () => preview.get('admins')),
      mapAdmin,
    ),
    createAdmin: (payload: { email: string; password: string; roles: Role[]; active: boolean }) => request<AdminUser>(
      '/governance/admins',
      { method: 'POST', body: payload },
    ),
    updateAdmin: (adminId: string, payload: { roles?: Role[]; active?: boolean }) => request<AdminUser>(
      `/governance/admins/${encodeURIComponent(adminId)}`,
      { method: 'PATCH', body: payload },
    ),
    revokeAdminSessions: (adminId: string) => request<{ admin: AdminUser; sessions_revoked: boolean }>(
      `/governance/admins/${encodeURIComponent(adminId)}/sessions/revoke`,
      { method: 'POST' },
    ),
    providersPage: (query: CollectionQuery) => requestCollection<ManagedProvider>('/governance/providers', query, () => preview.get('providers')),
    createProvider: (payload: Omit<ManagedProvider, 'id' | 'revision' | 'updated_at'>) => request<ManagedProvider>(
      '/governance/providers',
      { method: 'POST', body: payload },
    ),
    updateProvider: (provider: ManagedProvider, changes: Record<string, unknown>) => request<ManagedProvider>(
      `/governance/providers/${encodeURIComponent(provider.id)}`,
      { method: 'PATCH', body: { expected_revision: provider.revision, changes } },
    ),
    providerProbes: () => request<Array<{ id: string; name: string; provider_type: string; status: string; latency_ms: number; detail: string }>>('/governance/providers/probes'),
    costs: (query: CursorCollectionQuery) => requestCursorCollection<CostEvent>('/governance/costs', query),
    costSummary: () => requestSummary<{
      totals_micros: Record<string, number>
      monthly_commitments: Array<Record<string, unknown>>
    }, { monthly_commitments: SummaryCollectionMeta }>('/governance/costs/summary', ['monthly_commitments']),
    budgetsPage: (query: CollectionQuery) => requestCollection<ManagedBudget>('/governance/budgets', query),
    createBudget: (payload: { scope: string; period: 'daily' | 'monthly'; metric: ManagedBudget['metric']; limit_micros: number | null; limit_quantity: number | null; warning_percent: number; currency: string; enabled: boolean }) => request<ManagedBudget>(
      '/governance/budgets',
      { method: 'POST', body: payload },
    ),
    updateBudget: (budget: ManagedBudget, changes: Pick<ManagedBudget, 'limit_micros' | 'limit_quantity' | 'warning_percent' | 'enabled'>) => request<ManagedBudget>(
      `/governance/budgets/${encodeURIComponent(budget.id)}`,
      { method: 'PATCH', body: { expected_revision: budget.revision, changes } },
    ),
    disableBudget: (budget: ManagedBudget, reason: string) => request<ManagedBudget>(
      `/governance/budgets/${encodeURIComponent(budget.id)}/disable`,
      { method: 'POST', body: { expected_revision: budget.revision, reason } },
    ),
    budgetReservations: (query: CursorCollectionQuery, status?: string) => requestCursorCollection<BudgetReservation>(
      `/governance/budget-reservations${status ? `?status=${encodeURIComponent(status)}` : ''}`,
      query,
    ),
    reconcileBudgetReservation: (item: BudgetReservation, decision: 'release' | 'retain_unknown', reason: string) => request<BudgetReservation>(
      `/governance/budget-reservations/${encodeURIComponent(item.id)}/reconcile`,
      { method: 'POST', body: { expected_revision: item.revision, decision, reason } },
    ),
    idempotency: (query: CursorCollectionQuery, state?: string) => requestCursorCollection<IdempotencyReceipt>(
      `/governance/idempotency${state ? `?state=${encodeURIComponent(state)}` : ''}`,
      query,
    ),
    abandonIdempotency: (item: IdempotencyReceipt, reason: string) => request<IdempotencyReceipt>(
      `/governance/idempotency/${encodeURIComponent(item.namespace_hash)}/abandon`,
      { method: 'POST', body: { expected_created_at: item.created_at, reason } },
    ),
    rateLimits: (query: CursorCollectionQuery, policy?: string) => requestCursorCollection<RateLimitWindow>(
      `/governance/rate-limits${policy ? `?policy=${encodeURIComponent(policy)}` : ''}`,
      query,
    ),
    outbox: (query: CursorCollectionQuery, state?: string) => requestCursorCollection<OutboxRecord>(
      `/governance/outbox${state ? `?state=${encodeURIComponent(state)}` : ''}`,
      query,
    ),
    requeueOutbox: (item: OutboxRecord, reason: string) => request<OutboxRecord>(
      `/governance/outbox/${encodeURIComponent(item.id)}/requeue`,
      { method: 'POST', body: { expected_attempts: item.attempts, reason } },
    ),
    signingKeysPage: (query: CollectionQuery) => requestCollection<ManagedSigningKey>('/governance/devices/signing-keys', query, () => preview.get('signingKeys')),
    createSigningKey: (payload: { key_id: string; name: string; public_key_base64: string; hardware_revisions: string[] }) => request<ManagedSigningKey>(
      '/governance/devices/signing-keys',
      { method: 'POST', body: payload },
      () => preview.get('createSigningKey', { key: payload }),
    ),
    revokeSigningKey: (key: ManagedSigningKey, reason: string) => request<ManagedSigningKey>(
      `/governance/devices/signing-keys/${encodeURIComponent(key.id)}/revoke`,
      { method: 'POST', body: { expected_revision: key.revision, reason } },
      () => preview.get('revokeSigningKey', { key, reason }),
    ),
    firmwarePage: (query: CollectionQuery) => requestCollection<ManagedFirmware>('/governance/devices/firmware', query, () => preview.get('firmware')),
    registerFirmware: (payload: {
      version: string
      artifact_asset_id: string
      signature_asset_id: string
      signing_key_id: string
      sha256: string
      size_bytes: number
      hardware_revision: string
      release_notes: string
    }) => request<ManagedFirmware>(
      '/governance/devices/firmware',
      { method: 'POST', body: payload },
      () => preview.get('registerFirmware', { firmware: payload }),
    ),
    revokeFirmware: (item: ManagedFirmware, reason: string) => request<ManagedFirmware>(
      `/governance/devices/firmware/${encodeURIComponent(item.id)}/revoke`,
      { method: 'POST', body: { expected_revision: item.revision, reason } },
      () => preview.get('revokeFirmware', { firmware: item, reason }),
    ),
    otaRolloutsPage: (query: CollectionQuery) => requestCollection<ManagedOtaRollout>('/governance/devices/ota/rollouts', query, () => preview.get('otaRollouts')),
    createOtaRollout: (payload: Omit<ManagedOtaRollout, 'id' | 'revision' | 'updated_at'>) => request<ManagedOtaRollout>(
      '/governance/devices/ota/rollouts',
      { method: 'POST', body: payload },
    ),
    transitionOta: (rolloutId: string, action: 'start' | 'pause' | 'resume' | 'rollback') => request<ManagedOtaRollout>(
      `/governance/devices/ota/rollouts/${encodeURIComponent(rolloutId)}/transition`,
      { method: 'POST', body: { action } },
    ),
    otaHistory: async (rolloutId: string, query: CursorCollectionQuery): Promise<CursorCollectionPage<OtaCommandHistory>> => {
      const params = new URLSearchParams({ limit: String(query.limit) })
      if (query.cursor) params.set('after_cursor', query.cursor)
      const payload = await request<{ data: unknown; request_id?: string; meta?: { count?: number; next_cursor?: string | null; has_more?: boolean } }>(
        `/governance/devices/ota/rollouts/${encodeURIComponent(rolloutId)}/history?${params}`,
        { preserveEnvelope: true },
      )
      if (!payload.meta || typeof payload.meta.has_more !== 'boolean' || !('next_cursor' in payload.meta)) {
        throw new ApiError('OTA 历史缺少 next_cursor / has_more 元数据。', 502, undefined, 'invalid_ota_history_contract')
      }
      try {
        return normalizeCursorCollectionPage<OtaCommandHistory>({
          data: payload.data as OtaCommandHistory[],
          request_id: payload.request_id,
          meta: {
            count: payload.meta.count,
            next_cursor: payload.meta.next_cursor,
            has_next: payload.meta.has_more,
          },
        }, query)
      } catch (error) {
        if (error instanceof ApiError) {
          throw new ApiError(error.message, error.status, error.requestId, 'invalid_ota_history_contract', error.details)
        }
        throw error
      }
    },
    health: async () => {
      const payload = await request<RawGovernanceHealth>('/governance/health', {}, () => preview.get<RawGovernanceHealth>('governanceHealth'))
      if (payload.services) return { ...payload, services: payload.services }
      return { ...payload, services: healthServices(payload) }
    },
    audit: (query: CursorCollectionQuery) => requestCursorCollection<AuditRecord>('/governance/audit', query, () => preview.get('governanceAudit')),
  },
}
